import { useState } from "react";
import {
  APP_RELEASE_TRANSITIONS,
  appReleaseNextStatuses,
  type AppReleaseStatus
} from "../../shared/app-release";
import { isOfflineMutation } from "./model";
import type { StaffPaneProps } from "./console-types";
import "./app-release-card.css";

/**
 * The partner records where the release stands (F136b + F137 Q5/Q6).
 *
 * 🔴 Pavel's rule for who clicks what: "kde výsledek vidí ZÁKAZNÍK, odklikává
 * zákazník; kde ho vidíme MY, odklikává parťák." Nobody but us can see a store
 * review, so this control lives here and the client's own page carries no
 * control at all.
 *
 * The buttons ARE the catalog: only the moves the platform's own
 * `STATUS_TRANSITIONS` allows from the current state are drawn, so the console
 * can never offer what the server answers with a 400 (the P-18 law - a closed
 * control says why, and a control that cannot work is not drawn).
 */
const ERROR_CODES = ["release_transition_invalid", "store_links_required", "app_not_included"] as const;

export function AppReleaseCard(props: StaffPaneProps) {
  const track = props.detail.appTrack;
  const status: AppReleaseStatus = track?.release.status ?? "draft";
  const [busy, setBusy] = useState<AppReleaseStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ios, setIos] = useState("");
  const [android, setAndroid] = useState("");

  const nextStatuses = appReleaseNextStatuses(status);
  // Going live is the one move that carries evidence, so it gets the fields.
  const askingForLinks = nextStatuses.includes("live");

  async function move(target: AppReleaseStatus) {
    setBusy(target);
    setError(null);
    const storeUrls: Record<string, string> = {};
    if (target === "live") {
      if (ios.trim()) storeUrls.ios = ios.trim();
      if (android.trim()) storeUrls.android = android.trim();
    }
    const response = await props.run(
      `/api/staff/clients/${props.detail.client.id}/app-release/status`,
      { method: "POST", body: { status: target, storeUrls } }
    );
    if (response.ok) {
      setIos("");
      setAndroid("");
      await props.refresh();
    } else if (isOfflineMutation(response)) {
      setError(props.t("staffConsole.mutationOffline"));
    } else {
      let code = "";
      try {
        const body = await response.json() as { error?: string };
        if (typeof body.error === "string") code = body.error;
      } catch { /* the generic sentence below already covers an unreadable body */ }
      // 🔴 An allowlist, never a probe of whether `t()` returned its own key:
      // `t` THROWS on a missing key and a throw inside the handler would leave
      // the button disabled forever with nothing said (the F106 trap).
      const named = (ERROR_CODES as readonly string[]).includes(code);
      setError(props.t(named
        ? `staffConsole.appRelease.error.${code}`
        : "staffConsole.appRelease.error.generic"));
    }
    setBusy(null);
  }

  return (
    <article className="staff-card staff-phase-card" data-app-phase="release" data-release-status={status}>
      <h3>{props.t("staffConsole.appPhase.release.title")}</h3>
      <p className="staff-card-hint">{props.t(`staffConsole.appRelease.state.${status}`)}</p>

      {nextStatuses.length === 0 && (
        <p className="staff-card-hint">{props.t("staffConsole.appRelease.terminal")}</p>
      )}

      {askingForLinks && (
        <div className="app-release-links-form">
          <p className="staff-card-hint">{props.t("staffConsole.appRelease.linksHint")}</p>
          <label>
            <span>{props.t("staffConsole.appRelease.iosLabel")}</span>
            <input
              type="url"
              value={ios}
              placeholder="https://apps.apple.com/..."
              onChange={(event) => setIos(event.target.value)}
            />
          </label>
          <label>
            <span>{props.t("staffConsole.appRelease.androidLabel")}</span>
            <input
              type="url"
              value={android}
              placeholder="https://play.google.com/..."
              onChange={(event) => setAndroid(event.target.value)}
            />
          </label>
        </div>
      )}

      {nextStatuses.length > 0 && (
        <div className="app-release-actions">
          {nextStatuses.map((target) => (
            <button
              key={target}
              type="button"
              className={target === "live" ? "primary-button" : "secondary-button"}
              disabled={busy !== null}
              onClick={() => void move(target)}
            >
              {busy === target
                ? props.t("staffConsole.appRelease.working")
                : props.t(`staffConsole.appRelease.action.${target}`)}
            </button>
          ))}
        </div>
      )}

      {error && <p className="error-message" role="alert">{error}</p>}
    </article>
  );
}

/** Every status the catalog knows, for the guards. */
export const APP_RELEASE_STATUS_KEYS = Object.keys(APP_RELEASE_TRANSITIONS) as AppReleaseStatus[];
